import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import SwalPlugin from './plugins/SwalLoding'
import GlobalObj from './plugins/GlobalObj';
import VTooltip from 'v-tooltip';
import './assets/styles/global/PublicCss.js';

const app = createApp(App);

app.use(router);
app.use(SwalPlugin);
app.use(GlobalObj);
app.use(VTooltip);

// 전역 메시지 시스템
const globalMessageSystem = {
    messages: [],
    listeners: [],
    currentUserId: null,

    // 현재 사용자 ID 설정
    setUserId(userId) {
        if (this.currentUserId !== userId) {
            this.currentUserId = userId;
            this.loadMessages();
        }
    },

    // 스토리지 키 생성
    getStorageKey() {
        return this.currentUserId ? `globalMessages_${this.currentUserId}` : 'globalMessages_temp';
    },

    addMessage(messageData) {
        if (!this.currentUserId) {
            console.warn('사용자 ID가 설정되지 않았습니다. 메시지를 저장할 수 없습니다.');
            return;
        }

        const message = {
            id: Date.now() + Math.random(),
            type: messageData.type || 'info',
            title: messageData.title || '알림',
            text: messageData.text || '',
            details: messageData.details || '',
            timestamp: new Date(),
            isRead: false,
            userId: this.currentUserId
        };

        this.messages.unshift(message);

        // 최대 50개 메시지만 유지
        if (this.messages.length > 50) {
            this.messages = this.messages.slice(0, 50);
        }

        // 리스너들에게 알림
        this.listeners.forEach(callback => callback(this.messages));

        // localStorage에 사용자별로 저장
        localStorage.setItem(this.getStorageKey(), JSON.stringify(this.messages));
    },

    // 현재 메시지 리스트 반환
    getMessages() {
        return this.messages;
    },

    // 메시지 읽음 처리
    markAsRead(messageId) {
        const message = this.messages.find(msg => msg.id === messageId);
        if (message) {
            message.isRead = true;
            this.listeners.forEach(callback => callback(this.messages));
            localStorage.setItem(this.getStorageKey(), JSON.stringify(this.messages));
        }
    },

    // 모든 메시지 읽음 처리
    markAllAsRead() {
        this.messages.forEach(msg => {
            msg.isRead = true;
        });
        this.listeners.forEach(callback => callback(this.messages));
        localStorage.setItem(this.getStorageKey(), JSON.stringify(this.messages));
    },

    // 특정 메시지 삭제
    deleteMessage(messageId) {
        this.messages = this.messages.filter(msg => msg.id !== messageId);
        this.listeners.forEach(callback => callback(this.messages));
        localStorage.setItem(this.getStorageKey(), JSON.stringify(this.messages));
    },

    // 모든 메시지 삭제
    clearAllMessages() {
        this.messages = [];
        this.listeners.forEach(callback => callback(this.messages));
        localStorage.setItem(this.getStorageKey(), JSON.stringify(this.messages));
    },

    // 현재 사용자의 메시지 완전 삭제 (로그아웃 시)
    clearUserMessages() {
        this.messages = [];
        this.listeners.forEach(callback => callback(this.messages));
        
        // 현재 사용자의 메시지 localStorage에서 삭제
        if (this.currentUserId) {
            localStorage.removeItem(this.getStorageKey());
        }
        
        this.currentUserId = null;
    },

    // 메시지 변경을 감지하는 리스너
    subscribe(callback) {
        this.listeners.push(callback);
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    },

    // 메시지 로드, 복원
    loadMessages() {
        if (!this.currentUserId) {
            this.messages = [];
            return;
        }

        const saved = localStorage.getItem(this.getStorageKey());
        if (saved) {
            try {
                const loadedMessages = JSON.parse(saved);
                // 현재 사용자의 메시지만 필터링
                this.messages = loadedMessages
                    .filter(msg => !msg.userId || msg.userId === this.currentUserId)
                    .map(msg => ({
                        ...msg,
                        timestamp: new Date(msg.timestamp)
                    }));
            } catch (error) {
                console.error('메시지 로드 오류:', error);
                this.messages = [];
            }
        } else {
            this.messages = [];
        }

        // 리스너들에게 알림
        this.listeners.forEach(callback => callback(this.messages));
    }
};

// 앱에 전역 메시지 시스템 제공
app.provide('globalMessageSystem', globalMessageSystem);

// 폰트 로딩 확인
document.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('fonts-loaded');
    console.log('번들링된 Pretendard 폰트 사용 중');

    // 추가 안전장치: 폰트가 실제로 로드되었는지 확인
    if (document.fonts && document.fonts.check) {
        const isFontLoaded = document.fonts.check('16px Pretendard-Regular');
        if (!isFontLoaded) {
            console.warn('Pretendard 폰트 로딩 확인 실패, 시스템 폰트 사용');
            document.body.classList.add('fonts-fallback');
        }
    }
});

app.mount('#app');